import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import type { PricelistCaseResponse } from '@erp/rpc';
import { rpcClient } from '@/api/rpc/client';
import { Label } from '@/components/ui/label';
import { PricelistCaseVendorSelector } from './PricelistCaseVendorSelector';
import {
  pricelistCaseQueryKey,
  refreshPricelistCaseViews,
  rememberPricelistCase,
  withPricelistCaseVendors,
} from './pricelistCaseQueryCache';
import {
  caseVendorsFromLegacy,
  pricelistCaseVendorsUpdate,
  type PricelistCaseVendor,
} from './pricelistCaseVendors';

const UPDATE_CASE_VENDORS_MUTATION_KEY = ['pricelist-case-vendors', 'update'];

/**
 * The case sidebar's Vendors field: the shared vendor selector, saving every
 * change (a chip's ×, or the picker closing) as one `cases.update`.
 *
 * Optimistic: the case in the query cache takes the new vendor set before the
 * request leaves, so chips and the picker change on the click; a failed save
 * rolls back and says so. Once the last in-flight save settles, the case,
 * list and timeline refresh in the background to reconcile.
 */
export function PricelistCaseVendorsField({
  pricelistCase,
  onFailed,
}: {
  pricelistCase: PricelistCaseResponse;
  onFailed: (title: string, error: unknown) => void;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const caseId = pricelistCase.id;
  const vendors = caseVendorsFromLegacy(
    pricelistCase.companyId,
    pricelistCase.companyName,
    pricelistCase.vendors,
  );

  // Which save is the newest. A save only writes the cache — its server
  // result, or its rollback — while it is still the newest, so an older save
  // finishing late can't overwrite a newer one, whatever order they land in.
  const latestSave = useRef(0);

  const updateMutation = useMutation({
    mutationKey: UPDATE_CASE_VENDORS_MUTATION_KEY,
    mutationFn: (next: PricelistCaseVendor[]) =>
      rpcClient.inventoryManagement.pricelistCases.update({
        caseId,
        ...pricelistCaseVendorsUpdate(next),
        details: {},
      }),
    onMutate: async (next) => {
      latestSave.current += 1;
      const revision = latestSave.current;
      const queryKey = pricelistCaseQueryKey(caseId);
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<PricelistCaseResponse>(queryKey);
      if (previous !== undefined) {
        queryClient.setQueryData(
          queryKey,
          withPricelistCaseVendors(previous, next),
        );
      }
      return { previous, revision };
    },
    onError: (error, _next, context) => {
      if (
        context?.previous !== undefined &&
        context.revision === latestSave.current
      ) {
        rememberPricelistCase(queryClient, context.previous);
      }
      onFailed(
        gt('Could not update vendors', {
          context:
            'toast title when adding or removing a vendor on a price change fails',
        }),
        error,
      );
    },
    onSuccess: (updated, _next, context) => {
      if (context.revision === latestSave.current) {
        rememberPricelistCase(queryClient, updated);
      }
    },
    onSettled: () => {
      const pending = queryClient.isMutating({
        mutationKey: UPDATE_CASE_VENDORS_MUTATION_KEY,
        exact: true,
      });
      if (pending <= 1) refreshPricelistCaseViews(queryClient);
    },
  });

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        <T context="field label for the vendors on a pricelist update case">
          Vendors
        </T>
      </Label>
      <PricelistCaseVendorSelector
        vendors={vendors}
        onChange={(next) => updateMutation.mutate(next)}
        keepLastVendor
        triggerPlaceholder={gt('Add vendor', {
          context:
            'placeholder on the vendor picker that adds another vendor to a price change',
        })}
      />
    </div>
  );
}
